import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { HeartHandshake, RefreshCw, TrendingDown, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const TEAL = "#1F6F78";
const SAGE = "#3B6B4A";
const SAND = "#B7791F";
const PLUM = "#6B5CA5";
const CLAY = "#8A5A2E";
const PIE_COLORS = [TEAL, SAGE, SAND, PLUM, CLAY];

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
];

function Panel({ title, subtitle, children, testId }) {
  return (
    <section data-testid={testId} className="rounded-2xl border border-[#E3DED4] bg-white p-4 sm:p-5">
      <div>
        <h3 className="font-display text-base font-bold text-[#1F2A2E]">{title}</h3>
        {subtitle && <p className="text-xs text-[#7A868B] mt-0.5">{subtitle}</p>}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Stat({ label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-[#E3DED4] bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#7A868B]">{label}</p>
      <p className="font-display text-3xl font-bold text-[#1F2A2E] mt-2">{value}</p>
      {sub && <p className="text-xs text-[#7A868B] mt-1">{sub}</p>}
    </div>
  );
}

function FunnelStages({ stages, color, testId }) {
  const top = stages[0]?.count || 0;
  return (
    <div className="space-y-3" data-testid={testId}>
      {stages.map((s, i) => {
        const width = top ? Math.max(6, (s.count / top) * 100) : 6;
        return (
          <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="font-semibold text-[#1F2A2E]">
                <span className="text-[#7A868B] mr-1.5">{i + 1}.</span>
                {s.label}
              </span>
              <span className="shrink-0 text-[#4B5A60]">
                <strong className="text-[#1F2A2E]">{s.count}</strong>
                <span className="text-xs text-[#7A868B]"> · {s.pct_of_visitors}% of visitors</span>
              </span>
            </div>
            <div className="mt-1.5 h-6 rounded-lg bg-[#F0EDE6] overflow-hidden">
              <div
                className="h-full rounded-lg transition-transform duration-200"
                style={{
                  width: `${width}%`,
                  backgroundColor: color,
                  opacity: 1 - i * 0.14,
                }}
              />
            </div>
            {i > 0 && (
              <p className="mt-1 text-xs text-[#7A868B] flex items-center gap-1">
                <TrendingDown className="w-3 h-3 text-[#B7791F]" />
                {s.pct_of_previous}% continued from the step above
                {s.drop_off > 0 && ` · ${s.drop_off} dropped off`}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

const tooltipStyle = {
  contentStyle: {
    borderRadius: 12,
    border: "1px solid #E3DED4",
    fontSize: 12,
    boxShadow: "0 8px 24px rgba(31,42,46,0.08)",
  },
  labelStyle: { color: "#1F2A2E", fontWeight: 700 },
};

// Hoisted so Recharts' shallow prop comparison does not see a new object on
// every render of the panel.
const LEGEND_STYLE = { fontSize: 11 };

export default function FunnelPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/funnel?days=${d}`);
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const t = data?.totals;
  const paths = data?.sponsor_paths;
  const pathData = paths
    ? [
        { label: "Signed up as a sponsor", views: paths.new_signup },
        { label: "Existing member became a sponsor", views: paths.existing_member },
      ].filter((r) => r.views > 0)
    : [];

  return (
    <div className="mt-6 space-y-5" data-testid="admin-funnel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-[#1F2A2E]">Signup funnel</h2>
          <p className="text-sm text-[#4B5A60]">
            How visitors turn into members — and how people become sponsors.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border-2 border-[#E3DED4] bg-white overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                data-testid={`funnel-range-${r.days}`}
                className={`px-3 py-2 text-sm font-semibold transition-colors duration-150 ${
                  days === r.days
                    ? "bg-[#F2FAFA] text-[#1F6F78]"
                    : "text-[#4B5A60] hover:bg-[#F7F4EF]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => load(days)}
            data-testid="funnel-refresh-btn"
            className="border-[#E3DED4] text-[#1F6F78]"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          testId="funnel-kpi-visitor-rate"
          label="Visitor → member"
          value={`${t?.visitor_to_signup_rate ?? 0}%`}
          sub={`${t?.signups_total ?? 0} signups from ${t?.visitors ?? 0} visitors`}
        />
        <Stat
          testId="funnel-kpi-browse-rate"
          label="Browsed → member"
          value={`${t?.browse_to_signup_rate ?? 0}%`}
          sub={`${t?.browsed_sponsors ?? 0} people browsed sponsors`}
        />
        <Stat
          testId="funnel-kpi-sponsor-signups"
          label="New sponsors"
          value={t?.signups_sponsor ?? 0}
          sub={`${t?.sponsor_profiles_created ?? 0} sponsor profiles published`}
        />
        <Stat
          testId="funnel-kpi-seeker-signups"
          label="New sponsees"
          value={t?.signups_sponsee ?? 0}
          sub={`${t?.viewed_pricing ?? 0} visitors viewed pricing`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="People looking for a sponsor"
          subtitle="Visit → browse directory → open a profile → signup page → account created"
          testId="funnel-seeker"
        >
          {data?.seeker_funnel ? (
            <FunnelStages stages={data.seeker_funnel} color={TEAL} testId="funnel-seeker-stages" />
          ) : null}
        </Panel>

        <Panel
          title="People becoming sponsors"
          subtitle='Visit → "Become a Sponsor" page → sponsor signup → profile published'
          testId="funnel-sponsor"
        >
          {data?.sponsor_funnel ? (
            <FunnelStages stages={data.sponsor_funnel} color={SAGE} testId="funnel-sponsor-stages" />
          ) : null}
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          title="How sponsors register"
          subtitle="Brand-new sponsor signups vs members who upgraded later"
          testId="funnel-sponsor-paths"
        >
          {pathData.length ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pathData}
                    dataKey="views"
                    nameKey="label"
                    innerRadius={45}
                    outerRadius={78}
                    paddingAngle={3}
                  >
                    {pathData.map((d, i) => (
                      <Cell key={d.label} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip {...tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {pathData.map((d, i) => (
                  <div key={d.label} className="flex items-center gap-2 text-sm">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                    />
                    <span className="text-[#4B5A60] flex-1">{d.label}</span>
                    <strong className="text-[#1F2A2E]">{d.views}</strong>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-[#7A868B]">
              No sponsor profiles published in this range yet.
            </p>
          )}
        </Panel>

        <Panel
          title="Signups & visitors over time"
          subtitle="Daily new members by type"
          testId="funnel-chart-series"
        >
          {data?.series?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.series} margin={{ top: 5, right: 8, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE8DF" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#7A868B" }}
                  interval="preserveStartEnd"
                  minTickGap={18}
                />
                <YAxis tick={{ fontSize: 11, fill: "#7A868B" }} allowDecimals={false} />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Line
                  type="monotone"
                  dataKey="visitors"
                  name="Visitors"
                  stroke={SAND}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="seeker_signups"
                  name="New sponsees"
                  stroke={TEAL}
                  strokeWidth={2.5}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="sponsor_signups"
                  name="New sponsors"
                  stroke={SAGE}
                  strokeWidth={2.5}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="sponsor_profiles"
                  name="Profiles published"
                  stroke={PLUM}
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : null}
        </Panel>

        <Panel
          title="Where new members came from"
          subtitle="First traffic source of visitors who signed up"
          testId="funnel-signup-sources"
        >
          {data?.signup_sources?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={data.signup_sources}
                layout="vertical"
                margin={{ top: 0, right: 16, left: 8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE8DF" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#7A868B" }} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={110}
                  tick={{ fontSize: 11, fill: "#4B5A60" }}
                />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="count" name="Signups" fill={TEAL} radius={[0, 6, 6, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-[#7A868B]">
              Signup sources appear once new members register with tracking on.
            </p>
          )}
        </Panel>
      </div>

      <Panel
        title="Recent sponsor registrations"
        subtitle="Who published a sponsor profile and how long it took them"
        testId="funnel-sponsor-table"
      >
        {data?.sponsor_registrations?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="funnel-sponsor-registrations-table">
              <thead className="bg-[#F7F4EF] text-left">
                <tr>
                  {["Sponsor", "Email", "Program", "Joined", "Profile live", "Time to publish", "Path"].map(
                    (h) => (
                      <th key={h} className="px-3 py-2 font-bold text-[#4B5A60] whitespace-nowrap">
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E3DED4]">
                {data.sponsor_registrations.map((r, i) => (
                  <tr key={`${r.email}-${i}`}>
                    <td className="px-3 py-2 font-semibold text-[#1F2A2E] whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        <HeartHandshake className="w-3.5 h-3.5 text-[#3B6B4A]" />
                        {r.username}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[#4B5A60]">{r.email}</td>
                    <td className="px-3 py-2 text-[#4B5A60]">{r.program || "—"}</td>
                    <td className="px-3 py-2 text-[#4B5A60] whitespace-nowrap">
                      {r.joined_at ? new Date(r.joined_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2 text-[#4B5A60] whitespace-nowrap">
                      {r.profile_at ? new Date(r.profile_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2 text-[#4B5A60] whitespace-nowrap">
                      {r.hours_to_publish === null
                        ? "—"
                        : r.hours_to_publish < 1
                        ? "under an hour"
                        : `${Math.round(r.hours_to_publish)} h`}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                          r.path === "new_signup"
                            ? "bg-[#E4F3EA] text-[#14603C]"
                            : "bg-[#F0EDE6] text-[#4B5A60]"
                        }`}
                      >
                        {r.path === "new_signup" ? "Signed up as sponsor" : "Upgraded later"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-[#7A868B]">
            <UserPlus className="w-4 h-4" />
            No new sponsor profiles in this range yet.
          </div>
        )}
      </Panel>
    </div>
  );
}
